import type { Post } from '@/@types/post'
import {
	filterLinkAttachments,
	filterPicAttachments,
	filterVideoAttachments,
} from '@/components/Attachment/utils'
import { MEDIA_META } from '@/constants/api'
import { type Attachment, AttachmentStatus, AttachmentType } from '@/states/attachment'
import queryClient from '@/utils/queryClient'
import { compact, isEmpty, isNil } from 'lodash-es'
import toast from 'react-hot-toast'
import type { PostCreatePayload, PostFormValue, PostUpdatePayload } from './types'

export function toPostFormValue(data: Post): PostFormValue {
	const videoURL = queryClient.getQueryData<string>([MEDIA_META, data.id])
	return {
		id: data.id,
		content: data.content,
		attachments: compact([
			videoURL && {
				id: videoURL,
				type: AttachmentType.VIDEO,
				status: AttachmentStatus.UPLOADED,
				cdn: {
					file: {
						fileUrl: videoURL,
					},
				},
			},
			...(data.pictures?.map((item) => ({
				id: item.key,
				type: AttachmentType.PICTURE,
				status: AttachmentStatus.UPLOADED,
				cdn: {
					file: {
						fileUrl: item.picUrl,
						key: item.key,
					},
				},
			})) || []),
			data.linkInfo && {
				id: data.linkInfo.linkUrl,
				type: AttachmentType.LINK,
				status: AttachmentStatus.UPLOADED,
				linkInfo: data.linkInfo,
			},
		]) as Attachment[],
		topic: data.topic,
	}
}

function fillAttachments(
	payload: PostCreatePayload | PostUpdatePayload,
	attachments: Attachment[]
) {
	const pictureAttachments = filterPicAttachments(attachments)
	const videoAttachments = filterVideoAttachments(attachments)

	const pictureKeys = compact(pictureAttachments.map((item) => item.cdn?.file.key))
	const hasPictures = !isEmpty(pictureKeys)
	const video = videoAttachments.slice(0, 1)[0]?.cdn?.file
	const hasVideo = !isNil(video?.key)

	const isAllFileAttachmentsUploaded =
		[...pictureAttachments, ...videoAttachments].every(
			(item) => item.status === AttachmentStatus.UPLOADED
		) &&
		pictureKeys.length === pictureAttachments.length &&
		!isEmpty(videoAttachments)
			? hasVideo
			: true

	if (isAllFileAttachmentsUploaded) {
		payload.pictureKeys = pictureKeys
		payload.videoKey = video?.key
	} else {
		toast('请等待附件上传完成后继续')
		throw new Error('请等待附件上传完成后继续')
	}

	const linkInfo = compact(filterLinkAttachments(attachments).map((item) => item.linkInfo))[0]
	if (linkInfo) {
		payload.linkInfo = {
			...linkInfo,
			supplementary: false,
			urlsInText: [],
			linkType: 'default',
		}
	}

	if ('id' in payload) {
		// is edit mode
		switch (true) {
			case !!video?.fileUrl:
				payload.editType = 'VIDEO'
				break
			case !isEmpty(pictureKeys):
				payload.editType = 'IMAGE'
				break
			case !!linkInfo:
				payload.editType = 'LINK'
				break
			default:
				payload.editType = 'TEXT'
				break
		}
	}

	return payload
}

export function buildCreatePayload({ content, attachments, topic }: PostFormValue) {
	let payload = {
		content,
		syncToPersonalUpdate: true,
		// coords: {
		//   coordType: 'wgs84',
		//   lat: '31.306659957829417',
		//   lng: '121.50809463891405',
		// },
	} as PostCreatePayload

	payload = fillAttachments(payload, attachments) as PostCreatePayload

	if (topic) {
		payload.submitToTopic = topic.id
	}

	return payload
}

export function buildUpdatePayload({
	id,
	content,
	attachments,
	topic,
}: PostFormValue): PostUpdatePayload {
	let payload = {
		id,
		content,
	} as PostUpdatePayload

	payload = fillAttachments(payload, attachments) as PostUpdatePayload

	if (topic) {
		payload.submitToTopic = topic.id
	}

	return payload
}
